(() => {
  if (window.vastThumbnailArrowsLoaded) return;
  window.vastThumbnailArrowsLoaded = true;

  const desktop = window.matchMedia('(min-width: 750px)');
  const reducedMotion = window.matchMedia(
    '(prefers-reduced-motion: reduce)'
  );

  const selector =
    '[id^="MainProduct-"] ' +
    'media-gallery[data-desktop-layout="thumbnail_slider"] ' +
    '> .thumbnail-slider';

  const instances = new Map();

  function setup(rail) {
    const list = rail.querySelector('.thumbnail-list');
    if (!list) return null;

    const controller = new AbortController();
    const options = { signal: controller.signal };
    let frame = 0;

    function createArrow(direction, label) {
      const button = document.createElement('button');

      button.type = 'button';
      button.className =
        `vast-thumb-arrow vast-thumb-arrow--${direction}`;
      button.setAttribute('aria-label', label);
      button.setAttribute('aria-controls', list.id);
      button.hidden = true;

      button.innerHTML = `
        <svg viewBox="0 0 24 24" fill="none"
          aria-hidden="true" focusable="false">
          <path d="m6 9 6 6 6-6"
            stroke="currentColor"
            stroke-width="2"
            stroke-linecap="round"
            stroke-linejoin="round"/>
        </svg>
      `;

      return button;
    }

    const up = createArrow('up', 'Scroll thumbnails up');
    const down = createArrow('down', 'Scroll thumbnails down');

    rail.insertBefore(up, list);
    rail.appendChild(down);

    function visibleItems() {
      return Array.from(list.children).filter(
        item =>
          item.matches('.thumbnail-list__item') &&
          item.getClientRects().length > 0
      );
    }

    function update() {
      const maximum = Math.max(
        0,
        list.scrollHeight - list.clientHeight
      );

      const show = desktop.matches && maximum > 2;

      if (up.hidden === show) up.hidden = !show;
      if (down.hidden === show) down.hidden = !show;

      up.disabled = list.scrollTop <= 2;
      down.disabled = list.scrollTop >= maximum - 2;
    }

    function scrollTo(top) {
      list.scrollTo({
        top,
        behavior: reducedMotion.matches ? 'auto' : 'smooth'
      });
    }

    function move(direction) {
      if (!desktop.matches) return;

      const item = visibleItems()[0];
      if (!item) return;

      const gap =
        parseFloat(getComputedStyle(list).rowGap) || 0;
      const step = item.getBoundingClientRect().height + gap;

      scrollTo(list.scrollTop + direction * step);
    }

    function revealSelected() {
      if (!desktop.matches) return;

      const button = list.querySelector('[aria-current="true"]');
      const item = button?.closest('.thumbnail-list__item');

      if (!item || !item.getClientRects().length) return;

      const itemRect = item.getBoundingClientRect();
      const listRect = list.getBoundingClientRect();

      if (itemRect.top < listRect.top - 1) {
        scrollTo(
          list.scrollTop + itemRect.top - listRect.top
        );
      } else if (itemRect.bottom > listRect.bottom + 1) {
        scrollTo(
          list.scrollTop + itemRect.bottom - listRect.bottom
        );
      }
    }

    function refreshLayout() {
      cancelAnimationFrame(frame);

      frame = requestAnimationFrame(() => {
        update();
        revealSelected();
      });
    }

    up.addEventListener('click', () => move(-1), options);
    down.addEventListener('click', () => move(1), options);

    list.addEventListener('scroll', update, {
      passive: true,
      signal: controller.signal
    });

    desktop.addEventListener('change', refreshLayout, options);

    /* Recalculate after viewport or thumbnail size changes */
    const resizeObserver = new ResizeObserver(refreshLayout);
    resizeObserver.observe(list);

    /* Keep the selected thumbnail visible after Dawn updates */
    const selectionObserver = new MutationObserver(refreshLayout);

    selectionObserver.observe(list, {
      subtree: true,
      childList: true,
      attributes: true,
      attributeFilter: ['aria-current', 'hidden', 'class']
    });

    refreshLayout();

    return {
      list,
      refresh: refreshLayout,

      destroy() {
        controller.abort();
        resizeObserver.disconnect();
        selectionObserver.disconnect();
        cancelAnimationFrame(frame);
        up.remove();
        down.remove();
      }
    };
  }

  function refreshGalleries() {
    for (const [rail, instance] of instances) {
      if (!rail.isConnected || !rail.contains(instance.list)) {
        instance.destroy();
        instances.delete(rail);
      }
    }

    document.querySelectorAll(selector).forEach(rail => {
      if (instances.has(rail)) {
        instances.get(rail).refresh();
        return;
      }

      const instance = setup(rail);
      if (instance) instances.set(rail, instance);
    });
  }

  function start() {
    const main = document.getElementById('MainContent');
    if (!main) return;

    let queued = false;

    /* Handle product content replaced by Dawn or the theme editor */
    const observer = new MutationObserver(() => {
      if (queued) return;
      queued = true;

      requestAnimationFrame(() => {
        queued = false;
        refreshGalleries();
      });
    });

    observer.observe(main, {
      childList: true,
      subtree: true
    });

    document.addEventListener(
      'shopify:section:load',
      refreshGalleries
    );

    refreshGalleries();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start, {
      once: true
    });
  } else {
    start();
  }
})();